import moment from "moment";
import React from "react";

import { PhosphorUsersThree } from "components/Atoms/PhosphorIcon";
import { PhosphorLinkBreakButton } from "components/Atoms/PhosphorIconButton";
import { Toggle } from "components/Atoms/Toggle";
import { FlightSidePanel } from "components/SidePanel/Flight/FlightSidePanel";
import { TrainSidePanel } from "components/SidePanel/Train/TrainSidePanel";
import { GroundTransportSidePanel } from "components/SidePanel/GroundTransport/GroundSidePanel";
import { FormatArrivalTime } from "flights/lookup/FlightSearchResult";
import { AppointmentPhase } from "../../components/";
import { formatTime } from "utilities";

interface Props {
  canLinkUnlinkTravel: boolean
  appointment: server.dto.AppointmentForList
  displayInAdvancing: boolean
  unlink: (appointmentId: number) => void
  setDisplayInAdvancing: (appointment: server.dto.AppointmentForList, displayInAdvancing: boolean) => void
}

const formatAirport = (location: string, code: string) => code ? `${location} [${code}]` : location;

export const getLinkedTravelInfo = (appointment: server.dto.AppointmentForList) => {
  switch (appointment.type) {
    case "flight": {
      const flight = appointment.flight;
      return {
        id: flight.flightId,
        base: flight,
        summary: `${formatAirport(flight.departureAirportLocation, flight.departureAirportCode)} » ${formatAirport(flight.arrivalAirportLocation, flight.arrivalAirportCode)}`,
        description: [RESX.Flight.resxFlight, flight.flightDesignator].filter(Boolean).join(" ")
      };
    }
    case "train": {
      const train = appointment.train;
      return {
        id: train.trainId,
        base: train,
        summary: `${train.departureStationName} » ${train.arrivalStationName}`,
        description: [RESX.Train.resxTrain, train.trainNumber].filter(Boolean).join(" ")
      };
    }
    case "groundTransport": {
      const groundTransport = appointment.groundTransport;
      const hasPlaceNames = Boolean(groundTransport.departurePlaceName) || Boolean(groundTransport.arrivalPlaceName);
      return {
        id: groundTransport.groundTransportId,
        base: groundTransport,
        summary: `${groundTransport.departurePlaceType} » ${groundTransport.arrivalPlaceType}`,
        description: hasPlaceNames ? `${groundTransport.departurePlaceName ?? ""} » ${groundTransport.arrivalPlaceName ?? ""}`.trim() : null
      };
    }
    default:
      throw new Error(`Unexpected linked travel type: ${appointment.type}`);
  }
};

const LinkedTravelSidePanel = ({ appointment, children }: { appointment: server.dto.AppointmentForList, children: JSX.Element }) => {
  switch (appointment.type) {
    case "flight":
      return <FlightSidePanel flightId={appointment.flight.flightId} trigger={children} />;
    case "train":
      return <TrainSidePanel trainId={appointment.train.trainId} trigger={children} />;
    case "groundTransport":
      return <GroundTransportSidePanel groundTransportId={appointment.groundTransport.groundTransportId} trigger={children} />;
    default:
      throw new Error(`Unexpected linked travel type: ${appointment.type}`);
  }
};

class LinkedTravelRowCard extends React.Component<Props> {
  unlink = () => {
    const { unlink, appointment } = this.props;
    unlink(getLinkedTravelInfo(appointment).id);
  };

  render(): JSX.Element {
    const {
      appointment,
      displayInAdvancing,
      setDisplayInAdvancing,
      canLinkUnlinkTravel
    } = this.props;

    const { base, summary, description } = getLinkedTravelInfo(appointment);

    const startDate = moment.utc(base.dateTime.startDisplay);
    const endDate = moment.utc(base.dateTime.endDisplay);

    return <div className="flex gap-larger card-surface-standalone padding-large radius">
      <div>
        <AppointmentPhase value={base.appointmentPhase} displayLabel={false} />
      </div>
      <div className="flex-column gap grow1">
        <div className="flex gap-bigger align-center">
          <div className="flex-column gap-mini" style={{ flex: "0 0 122px" }}>
            {base.dateTime.mode === "hasTimeStartsInNight" && <div className="text-sublabel">{RESX.DateTime.InTheNightOf}</div>}
            <div className="big_text">{startDate.format("ll")}</div>
            <div className="flex text-sublabel">
              {formatTime(startDate)}&nbsp;-&nbsp;<FormatArrivalTime departure={startDate} arrival={endDate} />
            </div>
          </div>

          <div className="flex-column gap-mini grow1">
            <LinkedTravelSidePanel appointment={appointment}>
              <a onClick={() => { }}>{summary}</a>
            </LinkedTravelSidePanel>
            {description && <div className="s1_small">{description}</div>}
          </div>
        </div>

        {base.contactNames && <div className="flex align-center gap s1_small">
          <PhosphorUsersThree />
          {base.contactNames}
        </div>}
      </div>

      {canLinkUnlinkTravel && <div className="flex-column align-center gap-small linked-travel-toggle padding-left-larger padding-right-larger">
        <div className="text-sublabel">Display in advancing</div>
        <Toggle
          variant="icons"
          value={displayInAdvancing}
          onChange={(checked) => setDisplayInAdvancing(appointment, checked)} />
      </div>}

      {canLinkUnlinkTravel && <PhosphorLinkBreakButton
        layout="in-square"
        onClick={this.unlink} />}
    </div>;
  }
}

export default LinkedTravelRowCard;
